/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Loading} from '../../../components/Loading/Loading'
import {POST} from '../../../services/api'
import {Input} from '../../../components/Input/Input'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import Chart from 'react-apexcharts'
import moment from 'moment'
import {i18n} from '../../../translate/i18n'

type CouponUsageReport = {
  id: number
  nameCoupom: string
  typeCupom: 'classification' | 'product' | 'exclusive' | 'quantitative' | 'periodical' | 'cupomday'
  valueDiscount: string // Valor monetário como string "10.00"
  percentageDiscount: string // Porcentagem como string "20"
  usageQuantity: number // Limite de uso
  usedCount: number // Quantas vezes foi usado
  startDate: string // YYYY-MM-DD
  endDate: string // YYYY-MM-DD
  status: 'true' | 'false' // String para compatibilidade com badge
  created: string // YYYY-MM-DD
  // Campos calculados opcionais
  usagePercentage?: number // Porcentagem de uso (usedCount/usageQuantity * 100)
  isExpired?: boolean // Se o cupom está expirado
  remainingUses?: number // Usos restantes
}

type CouponUsageStats = {
  totalCoupons: number
  activeCoupons: number
  expiredCoupons: number
  totalUsages: number
  totalDiscount: number
}

type CouponChartData = {
  month: string
  usage: number
  discount: number
}

type DateFilter = 'today' | 'week' | 'month' | 'lastMonth' | 'year' | 'custom'

type selectMuliple = {
  value: string
  label: string
}

const PageContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [coupons, setCoupons] = useState<CouponUsageReport[]>([])
  const [stats, setStats] = useState<CouponUsageStats>({
    totalCoupons: 0,
    activeCoupons: 0,
    expiredCoupons: 0,
    totalUsages: 0,
    totalDiscount: 0,
  })
  const [chartData, setChartData] = useState<CouponChartData[]>([])
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'))
  const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'))
  const [dateFilter, setDateFilter] = useState<DateFilter>('today')
  const [couponTypes, setCouponTypes] = useState<selectMuliple[]>([]) // Array ao invés de string
  const [couponStatuses, setCouponStatuses] = useState<selectMuliple[]>([])
  const [typeDistribution, setTypeDistribution] = useState<any[]>([])

  // Configuração dos headers da tabela
  const headers = [
    {value: 'nameCoupom', key: 'Nome', type: 'default'},
    {
      value: 'typeCupom',
      key: i18n.t('coupons.type'),
      type: 'badge_v2',
      format: (val: any) => typeLabels[val] || val,
      badge: {
        classification: 'primary',
        product: 'success',
        exclusive: 'warning',
        quantitative: 'secondary',
        periodical: 'dark',
        cupomday: 'light',
      },
    },
    {value: 'valueDiscount', key: 'Desconto (R$)', type: 'money'},
    {value: 'percentageDiscount', key: 'Desconto (%)', type: 'default'},
    {value: 'usedCount', key: 'Vezes Usado', type: 'default'},
    {value: 'startDate', key: 'Data Início', type: 'date'},
    {value: 'endDate', key: 'Data Fim', type: 'date'},
    {
      value: 'status',
      key: i18n.t('coupons.status'),
      type: 'badge_v2',
      format: (val: any) => i18n.t(`coupons.statusLabels.${String(val)}`) || val,
      badge: {
        true: 'success',
        false: 'danger',
      },
    },
  ]

  // Opções para filtro de tipo de cupom
  const couponTypeOptions = [
    {value: 'all', select: i18n.t('coupons.typeOptions.all')},
    {value: 'classification', select: i18n.t('coupons.typeOptions.classification')},
    {value: 'product', select: i18n.t('coupons.typeOptions.product')},
    {value: 'exclusive', select: i18n.t('coupons.typeOptions.exclusive')},
    {value: 'quantitative', select: i18n.t('coupons.typeOptions.quantitative')},
    {value: 'periodical', select: i18n.t('coupons.typeOptions.periodical')},
    {value: 'cupomDay', select: i18n.t('coupons.typeOptions.cupomday')},
  ]

  const typeLabels: any = {
    classification: i18n.t('coupons.typeOptions.classification'),
    product: i18n.t('coupons.typeOptions.product'),
    exclusive: i18n.t('coupons.typeOptions.exclusive'),
    quantitative: i18n.t('coupons.typeOptions.quantitative'),
    periodical: i18n.t('coupons.typeOptions.periodical'),
    cupomday: i18n.t('coupons.typeOptions.cupomday'),
  }

  // Opções para filtro de status
  const statusOptions = [
    {value: 'all', select: i18n.t('coupons.statusOptions.all')},
    {value: 'active', select: i18n.t('coupons.statusOptions.active')},
    {value: 'inactive', select: i18n.t('coupons.statusOptions.inactive')},
    {value: 'expired', select: i18n.t('coupons.statusOptions.expired')},
  ]

  // Buscar dados do relatório
  const fetchReportData = async () => {
    try {
      setLoading(true)

      // Montar DTO conforme especificação
      const filterDTO: any = {
        startDate,
        endDate,

        ...(couponTypes.length > 0 && {type: couponTypes.map((e) => e.value)}), // Usar 'types' no plural
        ...(couponStatuses.length > 0 && {status: couponStatuses.map((e) => e.value)}), // Usar 'statuses' no plural
      }

      // Executa as requisições em paralelo
      const [couponsResponse, statsResponse, chartResponse] = await Promise.all([
        POST(`discount-coupons/report-usage`, filterDTO),
        POST(`discount-coupons/report-stats`, filterDTO),
        POST(`discount-coupons/report-chart`, filterDTO),
      ])

      setStats(statsResponse.data)
      setCoupons(couponsResponse.data || [])
      setChartData(chartResponse.data)

      const distribution = calculateTypeDistribution(couponsResponse.data || [])
      setTypeDistribution(distribution)
    } catch (error) {
      console.error('Erro ao buscar dados do relatório:', error)
      // Dados mock para demonstração
      // const mockCoupons: CouponUsageReport[] = [
      //   {
      //     id: 1,
      //     nameCoupom: 'DESCONTO10',
      //     typeCupom: 'quantitative',
      //     valueDiscount: '10.00',
      //     percentageDiscount: '0',
      //     usageQuantity: 100,
      //     usedCount: 45,
      //     startDate: '2024-01-01',
      //     endDate: '2024-12-31',
      //     status: 'true',
      //     created: '2024-01-01',
      //   },
      //   {
      //     id: 3,
      //     nameCoupom: 'FRETE_GRATIS',
      //     typeCupom: 'exclusive',
      //     valueDiscount: '15.50',
      //     percentageDiscount: '0',
      //     usageQuantity: 200,
      //     usedCount: 180,
      //     startDate: '2024-03-01',
      //     endDate: '2024-09-30',
      //     status: 'true',
      //     created: '2024-03-01',
      //   },
      // ]

      // const mockStats: CouponUsageStats = {
      //   totalCoupons: 25,
      //   activeCoupons: 18,
      //   expiredCoupons: 4,
      //   totalUsages: 1250,
      //   totalDiscount: 15750.5,
      // }

      // const mockChartData: CouponChartData[] = [
      //   {month: 'Jan', usage: 120, discount: 1200},
      //   {month: 'Fev', usage: 95, discount: 950},
      //   {month: 'Mar', usage: 140, discount: 1400},
      //   {month: 'Abr', usage: 110, discount: 1100},
      //   {month: 'Mai', usage: 160, discount: 1600},
      //   {month: 'Jun', usage: 85, discount: 850},
      // ]

      // setCoupons(mockCoupons)
      // setStats(mockStats)
      // setChartData(mockChartData)
    } finally {
      setLoading(false)
    }
  }

  // Exportar dados atuais da tabela para CSV (download client-side)
  const exportCSV = () => {
    try {
      const rows: string[] = []

      const headerLabels = headers.map((h: any) => h.key as string)
      const headerStr = headerLabels.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(',')
      rows.push(headerStr)
      ;(coupons || []).forEach((item: CouponUsageReport) => {
        const row = headers.map((h: any) => {
          const raw = (item as any)[h.value]
          const display = h.format ? h.format(raw, item) : raw
          const cell =
            display === undefined || display === null ? '' : String(display).replace(/"/g, '""')
          return `"${cell}"`
        })
        rows.push(row.join(','))
      })

      const csvContent = '\uFEFF' + rows.join('\r\n')
      const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `coupons_report_${moment().format('YYYYMMDD_HHmmss')}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Erro ao exportar CSV:', err)
    }
  }

  // Função para calcular distribuição por tipo dos dados reais
  const calculateTypeDistribution = (couponsData: CouponUsageReport[]) => {
    const distribution = couponsData.reduce((acc, coupon) => {
      const type = coupon.typeCupom
      if (!acc[type]) {
        acc[type] = {
          count: 0,
          usage: 0,
          totalDiscount: 0,
        }
      }
      acc[type].count += 1
      acc[type].usage += coupon.usedCount
      acc[type].totalDiscount +=
        parseFloat(coupon.valueDiscount || '0') +
        (parseFloat(coupon.percentageDiscount || '0') / 100) * coupon.usedCount * 50 // assumindo valor médio
      return acc
    }, {} as any)

    return Object.entries(distribution).map(([type, data]: [string, any]) => ({
      type,
      ...data,
    }))
  }

  // Aplicar filtro de data rápido
  const applyDateFilter = (filter: DateFilter) => {
    setDateFilter(filter)
    const today = moment()

    switch (filter) {
      case 'today':
        setStartDate(today.format('YYYY-MM-DD'))
        setEndDate(today.format('YYYY-MM-DD'))
        break
      case 'week':
        setStartDate(today.startOf('week').format('YYYY-MM-DD'))
        setEndDate(today.endOf('week').format('YYYY-MM-DD'))
        break
      case 'month':
        setStartDate(today.startOf('month').format('YYYY-MM-DD'))
        setEndDate(today.endOf('month').format('YYYY-MM-DD'))
        break
      case 'lastMonth':
        const lastMonth = today.subtract(1, 'month')
        setStartDate(lastMonth.startOf('month').format('YYYY-MM-DD'))
        setEndDate(lastMonth.endOf('month').format('YYYY-MM-DD'))
        break
      case 'year':
        setStartDate(today.startOf('year').format('YYYY-MM-DD'))
        setEndDate(today.endOf('year').format('YYYY-MM-DD'))
        break
    }
  }

  // Executar busca
  const handleSearch = () => {
    fetchReportData()
  }

  useEffect(() => {
    fetchReportData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (dateFilter !== 'custom') {
      fetchReportData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          {/* Filtros */}
          <div className='d-flex justify-content-between gap-5'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12 w-100'>
              <div className='card-header'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bolder fs-3 mb-1'>
                    {i18n.t('coupons.filters')}
                  </span>
                  <BreadCrumbs />
                </h3>
              </div>
              <div className='card-body'>
                <div className='d-flex gap-5 flex-wrap'>
                  <Input
                    inputAttr={{
                      type: 'date',
                    }}
                    label={i18n.t('coupons.dateInit')}
                    value={startDate}
                    change={(key: string) => {
                      setStartDate(key)
                      setDateFilter('custom')
                    }}
                  />
                  <Input
                    inputAttr={{
                      type: 'date',
                    }}
                    label={i18n.t('coupons.dateEnd')}
                    value={endDate}
                    change={(key: string) => {
                      setEndDate(key)
                      setDateFilter('custom')
                    }}
                  />
                  <Input
                    inputAttr={{
                      type: 'select-mult-v2',
                    }}
                    label={i18n.t('coupons.type')}
                    value={couponTypes}
                    change={(selectedOptions: any) => {
                      // selectedOptions será um array de objetos {value, label}
                      const values = selectedOptions ? selectedOptions.map((opt: any) => opt) : []
                      setCouponTypes(values)
                    }}
                    options={couponTypeOptions.filter((opt) => opt.value !== 'all')} // Remover "Todos" do multi-select
                  />
                  <Input
                    inputAttr={{
                      type: 'select-mult-v2',
                    }}
                    label={i18n.t('coupons.status')}
                    value={couponStatuses}
                    change={(selectedOptions: any) => {
                      // selectedOptions será um array de objetos {value, label}
                      const values = selectedOptions ? selectedOptions.map((opt: any) => opt) : []
                      setCouponStatuses(values)
                    }}
                    options={statusOptions.filter((opt) => opt.value !== 'all')} // Remover "Todos" do multi-select
                    placeholder={i18n.t('coupons.selectStatuses')}
                  />
                  <div className='d-flex align-items-end gap-3'>
                    <Button
                      text={i18n.t('coupons.actions.filter')}
                      color='primary'
                      size='small'
                      btnAttr={{
                        type: 'button',
                      }}
                      click={handleSearch}
                      btnClass='fw-bolder mb-1'
                      verticalPadding={3}
                    />
                    <Button
                      text={i18n.t('coupons.actions.export')}
                      color='success'
                      size='small'
                      btnAttr={{
                        type: 'button',
                      }}
                      click={exportCSV}
                      btnClass='fw-bolder mb-1'
                      verticalPadding={3}
                    />
                  </div>
                </div>

                {/* Filtros rápidos */}
                <div className='d-flex gap-2 mt-5 flex-wrap'>
                  <Button
                    text={i18n.t('coupons.quick.today')}
                    color={dateFilter === 'today' ? 'primary' : 'light'}
                    size='small'
                    click={() => applyDateFilter('today')}
                    btnClass='fw-bolder'
                    verticalPadding={2}
                  />
                  <Button
                    text={i18n.t('coupons.quick.week')}
                    color={dateFilter === 'week' ? 'primary' : 'light'}
                    size='small'
                    click={() => applyDateFilter('week')}
                    btnClass='fw-bolder'
                    verticalPadding={2}
                  />
                  <Button
                    text={i18n.t('coupons.quick.month')}
                    color={dateFilter === 'month' ? 'primary' : 'light'}
                    size='small'
                    click={() => applyDateFilter('month')}
                    btnClass='fw-bolder'
                    verticalPadding={2}
                  />
                  <Button
                    text={i18n.t('coupons.quick.lastMonth')}
                    color={dateFilter === 'lastMonth' ? 'primary' : 'light'}
                    size='small'
                    click={() => applyDateFilter('lastMonth')}
                    btnClass='fw-bolder'
                    verticalPadding={2}
                  />
                  <Button
                    text={i18n.t('coupons.quick.year')}
                    color={dateFilter === 'year' ? 'primary' : 'light'}
                    size='small'
                    click={() => applyDateFilter('year')}
                    btnClass='fw-bolder'
                    verticalPadding={2}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Estatísticas */}
          <div className='d-flex justify-content-between gap-5'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12 w-100'>
              <div className='card-header'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bolder fs-3 mb-1'>
                    {i18n.t('coupons.stats.title')}
                  </span>
                </h3>
              </div>
              <div className='card-body'>
                <div className='row g-6'>
                  <div className='col-xl-3 col-md-6'>
                    <div className='bg-light-primary rounded d-flex flex-column flex-center p-6 h-100'>
                      <span className='text-primary fs-2x fw-bold'>{stats.totalCoupons}</span>
                      <span className='text-muted fw-semibold fs-6'>
                        {i18n.t('coupons.stats.totalCoupons')}
                      </span>
                    </div>
                  </div>
                  <div className='col-xl-3 col-md-6'>
                    <div className='bg-light-success rounded d-flex flex-column flex-center p-6 h-100'>
                      <span className='text-success fs-2x fw-bold'>{stats.activeCoupons}</span>
                      <span className='text-muted fw-semibold fs-6'>
                        {i18n.t('coupons.stats.activeCoupons')}
                      </span>
                    </div>
                  </div>
                  <div className='col-xl-3 col-md-6'>
                    <div className='bg-light-info rounded d-flex flex-column flex-center p-6 h-100'>
                      <span className='text-info fs-2x fw-bold'>{stats.totalUsages}</span>
                      <span className='text-muted fw-semibold fs-6'>
                        {i18n.t('coupons.stats.totalUsages')}
                      </span>
                    </div>
                  </div>
                  <div className='col-xl-3 col-md-6'>
                    <div className='bg-light-warning rounded d-flex flex-column flex-center p-6 h-100'>
                      <span className='text-warning fs-2x fw-bold'>
                        R$ {stats.totalDiscount.toFixed(2)}
                      </span>
                      <span className='text-muted fw-semibold fs-6'>
                        {i18n.t('coupons.stats.totalDiscount')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de Tipos de Cupom */}
          <div className='d-flex justify-content-between gap-5'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12 w-100'>
              <div className='card-header'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bolder fs-3 mb-1'>
                    {i18n.t('coupons.distribution.title')}
                  </span>
                </h3>
              </div>
              <div className='d-flex align-items-center w-100 h-100 p-4'>
                <div className=''>
                  <Chart
                    options={{
                      colors: ['#009EF7', '#50CD89', '#FFC700', '#F1416C', '#7239EA', '#181C32'],
                      dataLabels: {enabled: true},
                      legend: {show: false},
                      labels: typeDistribution.map((item) => typeLabels[item.type] || item.type),
                    }}
                    series={typeDistribution.map((item) => item.count)}
                    type='donut'
                  />
                </div>
                <div className='w-75'>
                  <Chart
                    options={{
                      chart: {id: 'bar-chart'},
                      xaxis: {
                        categories: typeDistribution.map(
                          (item) => typeLabels[item.type] || item.type
                        ),
                      },
                      colors: ['#009EF7', '#50CD89', '#FFC700', '#F1416C', '#7239EA', '#181C32'],
                      plotOptions: {
                        bar: {distributed: true},
                      },
                      dataLabels: {enabled: true},
                      legend: {show: false},
                    }}
                    series={[
                      {
                        name: i18n.t('coupons.series.usageCount'),
                        data: typeDistribution.map((item) => item.usage),
                      },
                    ]}
                    type='bar'
                    height={220}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Gráfico de Uso Mensal */}
          <div className='d-flex justify-content-between gap-5'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12 w-100'>
              <div className='card-header'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bolder fs-3 mb-1'>
                    {i18n.t('coupons.monthlyUsage.title')}
                  </span>
                </h3>
              </div>
              <div className='d-flex align-items-center w-100 h-100 p-4'>
                <div className='w-100'>
                  <Chart
                    options={{
                      chart: {
                        id: 'monthly-usage',
                        type: 'line',
                      },
                      xaxis: {
                        categories: chartData.map((item) => item.month),
                      },
                      colors: ['#009EF7', '#FFC700'],
                      dataLabels: {
                        enabled: true,
                      },
                      stroke: {
                        curve: 'smooth',
                      },
                      legend: {
                        position: 'top',
                      },
                    }}
                    series={[
                      {
                        name: i18n.t('coupons.series.numberOfUses'),
                        data: chartData.map((item) => item.usage),
                      },
                      {
                        name: i18n.t('coupons.series.discountValue'),
                        data: chartData.map((item) => item.discount),
                      },
                    ]}
                    type='line'
                    height={350}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tabela de Cupons */}
          <div className='d-flex justify-content-between gap-5'>
            <div className='card card-xxl-stretch mb-5 mb-xl-12 w-100'>
              <div className='card-header'>
                <h3 className='card-title align-items-start flex-column'>
                  <span className='card-label fw-bolder fs-3 mb-1'>
                    {i18n.t('coupons.table.details')}
                  </span>
                </h3>
              </div>
              <div className='card-body'>
                <Datatable
                  data={coupons}
                  headers={headers}
                  color='primary'
                  search={true}
                  showControls={true}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const CouponsReportContent: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('MENU.DASHBOARD') || 'Dashboard', path: 'dashboard', isActive: true},
          {title: i18n.t('coupons.report'), path: 'coupons/relatorio', isActive: true},
        ]}
      >
        {i18n.t('coupons.report')}
      </PageTitle>
      <PageContent />
    </>
  )
}

export {CouponsReportContent}

export default CouponsReportContent
